import { Subscriptions, SubscriptionData } from './Subscription';
import { Availability, AvailabilityStatus } from './Availability';
import {
	Discount,
	Promotion,
	DiscountData,
	normalizePromotions,
	synthesizeCallouts,
	PROMOTION_PRIORITY,
	REBATE_EXEMPT_CATEGORIES,
	RawPromotionData,
} from './Promotion';
import { Ratings } from './Rating';
import Details, { FeatureGroups, ClassificationData } from './Details';

import 'etc/designs/base/clientlibs/shared/vendor/ba-debug.min.js';
import { compareNumbers, Case, Segment } from './util';
import makePrice, { HybrisPriceRepresentation } from 'lib/ecommerce/Price';
import { Texts } from 'lib/ecommerce/Tc.Module.EcommerceProductDataHub';

// Map NEC image size names to values
// FIXME NEC: This is not as specified. Revert once values come back correctly.
interface NecImageSizes {
	[sizeName : string] : number;
}
const NEC_IMAGE_SIZES : NecImageSizes = {
	product: 344,
	superZoom: 450,
	thumbnail: 180,
	zoom: 450,
};

interface ImageData {
	format : keyof NecImageSizes | number;
	sapUrl? : string;
	url : string;
}

// FIXME NEC: Fields missing
export interface UnimplementedFamilyData {
	sddAllowed : boolean | undefined;
	sddMinimumPurchaseAmount : number | undefined;
	sddShippingFee : number | undefined;
	isEordersProduct : boolean;
}

export interface FamilyData extends UnimplementedFamilyData {
	name : string;
	brand : {
		code : string;
	},
	description : string;
	slogan : string;
	rawMaterialSelection : string;
	generalTermsAndConditions : string;
	guaranteePeriod : number;
	productConditions : string;
	ecoPoints : string;
	code : string;
	productCategoryMaster : string;
	configurationNeeded : boolean;
	variantType : string;
	redFlagText : string;
	topFeatures : string;
}

export interface ProductData extends FamilyData {
	discountSpecialOffers? : DiscountData[];
	bundleSpecialOffers? : RawPromotionData[],
	price? : HybrisPriceRepresentation;
	productCategory2 : string;
	subscriptionBundles? : SubscriptionData[];
	images : ImageData[];
	classifications : ClassificationData[];
	deliveryFeatures : string;
	callout : {
		code : string;
		name : string;
	};
	guaranteeExtension? : WarrantyExtension;
	deliveryInfo : string;
	onStockNotifications : boolean;
	reservationInStoreAllowed : boolean;
	reservationAllowed : boolean;
}

export interface WarrantyExtension {
	price : number;
	guaranteePeriod? : number;
}

interface OverrideAdditions extends ProductData {
	colorText : string;
	color : string;
	memory : string;
	familyName : string;
	productName : string;
	productId : string;
	imagePath : string;
}

export type Overrides = {
	[key in keyof OverrideAdditions]? : OverrideAdditions[key];
};

/**
 * Wrapper object for one family product
 */
export class Product {
	private product : ProductData;
	private overrides : Overrides;
	private family : Family;
	private subscriptions : Subscriptions;
	private availability : Availability;
	private discountPrice : Discount;
	private promotions : Promotion[];
	private texts : Texts;
	private details : Details;
	private images : {
		[size : number] : ImageData;
	};
	constructor(
		product : ProductData,
		family : Family,
		featureGroups : FeatureGroups,
		bestseller : string,
		texts : Texts,
	) {
		this.product = product;
		this.overrides = {};
		this.family = family;
		this.texts = texts;

		this.subscriptions = new Subscriptions(product.subscriptionBundles);
		this.availability = new Availability(product.code);

		const discountPrices = (product.discountSpecialOffers || [])
			.map(discount => new Discount(discount))
			.filter(discount => discount.isValid());

		if(discountPrices.length > 1) {
			window.debug.error('Found more than one applicable discount for product', product.code);
		}
		// Add dummy discount of 0 so the API is more unified.
		this.discountPrice = discountPrices[0] || new Discount({active: false});

		const promotionDatas = normalizePromotions(product.bundleSpecialOffers);
		const callouts = synthesizeCallouts(product, bestseller, texts, promotionDatas, this.discountPrice);

		this.promotions = promotionDatas.concat(callouts)
			.map(promotion => new Promotion(promotion))
			.sort(function(ref, candidate) {
				return compareNumbers(
					PROMOTION_PRIORITY.indexOf(ref.getType()),
					PROMOTION_PRIORITY.indexOf(candidate.getType()),
				);
			});

		// FIXME NEC: Possibly move to ProductFamily
		this.images = {};
		for(const imageData of product.images || []) {
			const size = typeof(imageData.format) === 'string' ? NEC_IMAGE_SIZES[imageData.format] : imageData.format;
			this.images[size] = imageData;
		}

		this.details = new Details(product.classifications || [], product, family.productFamily, featureGroups);

		// Pre-emptive loading of product availability info
		// tslint:disable-next-line:no-floating-promises
		this.availability.load();
	}

	public setOverrides(ovr : Overrides) {
		this.overrides = ovr;
	}

	public getDetails() {
		return this.details;
	}

	public matches(color? : string, memory? : string | number, dimension? : string | number) {
		if (color && String(this.getColor()).toLowerCase() !== String(color).toLowerCase()) {
			return false;
		}
		if (memory && String(this.getMemory()).toLowerCase() !== String(memory).toLowerCase()) {
			return false;
		}
		const productDimension = String(
			this.getDetails().getDimension() && this.getDetails().getDimension()!.getValue(),
		).toLowerCase();
		if (dimension && productDimension !== String(dimension).toLowerCase()) {
			return false;
		}

		return true;
	}

	// Like getDetails().getColor().getValue() but includes overrides
	public getColor() {
		return this.overrides.color || this.details.getColor().getValue();
	}

	// Like getDetails().getColorText().getValue() but includes overrides
	public getColorText() {
		return this.overrides.colorText || this.details.getColorText().getValue();
	}

	// Like getDetails().getMemory().getValue() but includes overrides
	public getMemory() {
		return this.overrides.memory || this.details.getMemory().getValue();
	}

	// Like getDetails().getMemory().getValueFormatted() but includes overrides
	public getMemoryText() {
		const mem = this.details.getMemory();
		if(this.overrides.memory) {
			let unit = mem.getUnitSymbol();
			unit = unit ? ' ' + unit : '';

			// FIXME NEC: What if unit is part of memory already?
			return this.overrides.memory + unit;
		}

		return mem.getValueFormatted();
	}

	// FIXME NEC: Move to base class with ProductFamily?
	public getBrand() {
		return this.product.brand && this.product.brand.code;
	}

	public getName() {
		return this.overrides.productName || this.product.name;
	}

	public getFamilyName() {
		return this.overrides.familyName || this.family.getFamilyName() || this.getName();
	}

	// FIXME NEC: Move to base class with ProductFamily?
	public getCategory() {
		return this.product.productCategoryMaster;
	}

	public getProductCategory2() {
		return this.product.productCategory2;
	}

	public showsRebate() {
		return $.inArray(this.getCategory(), REBATE_EXEMPT_CATEGORIES) === -1;
	}

	public hasGuaranteeExtension() {
		return (
			this.product.guaranteeExtension &&
			this.product.guaranteeExtension.guaranteePeriod &&
			this.product.guaranteeExtension.price
		);
	}

	public getWarrantyPriceFormatted() {
		if(this.hasGuaranteeExtension()) {
			const price = makePrice(this.product.guaranteeExtension!.price);
			if(price.isValid()) {
				return `+ ${price.currency()} ${price.formatted()}`;
			}
		}

		return '';
	}

	public getWarrantyPeriod() {
		return (this.product.guaranteeExtension && this.product.guaranteeExtension.guaranteePeriod) || 0;
	}

	/**
	 * Gets the price info for how much the product costs without subscriptions (but including discounts)
	 * @returns Price
	 */
	public getDiscountedPrice() {
		if(!this.showsRebate()) {
			return this.getOriginalPrice();
		}

		return this.discountPrice.getPrice(this.getOriginalPrice());
	}

	/**
	 * Gets the price info for how much the product costs without subscriptions and without discounts
	 * @returns Price
	 */
	public getOriginalPrice() {
		return makePrice(this.product.price);
	}

	public isDiscounted() {
		return this.getDiscountedPrice() < this.getOriginalPrice();
	}

	// Gets the lowest price this product can be had for, taking any possible subscriptions into account
	public getMinimumPrice() {
		const prices = this.subscriptions.map(subscription => {
			// Math.min will call its valueOf automatically from the Price instance returned by getPrice()
			return subscription.getPrice();
		});
		// Add device price
		prices.push(this.getDiscountedPrice());

		return makePrice(Math.min(...(prices as any)));
	}

	/**
	 * Returns the price for a subscription or, as a fallback, the price of the
	 * Product if no subscription can be found
	 *
	 * @param subscriptionId
	 * @param subscriptionDuration
	 *
	 * @returns Price
	 */
	public getPrice(subscriptionId? : string, subscriptionDuration? : string | number) {
		const price = this.getSubscriptionPrice(subscriptionId, subscriptionDuration);

		if (!price) {
			return this.getDiscountedPrice();
		}

		return price;
	}

	public getSubscriptionPrice(subscriptionId? : string, subscriptionDuration? : string | number) {
		if(this.subscriptions.contains(subscriptionId, subscriptionDuration)) {
			return this.subscriptions.getPrice(subscriptionId, subscriptionDuration);
		}

		return null;
	}

	/**
	 * Retrieves the simple pay monthly pay from the provided subscriptionId and subscriptionDuration.
	 * Default value is -1 (if no plan found) or a Price with value of 0 (if the plan does not have simple pay)
	 * both of which mean simple pay should not be active.
	 *
	 * @param subscriptionId - The subscription Id to check
	 * @param subscriptionDuration - The subscription duration to check
	 * @returns {(string|Number)}
	 */
	public getSimplePayMonthlyPrice(subscriptionId? : string, subscriptionDuration? : string | number) {
		if (!subscriptionId || !this.subscriptions.getAll().length) {
			const price = this.subscriptions.getSimplePayRateWithoutSubscription();
			if (price.value() === 0) {
				return -1;
			}

			return price;
		}
		const plan = this.subscriptions.get(subscriptionId, subscriptionDuration);
		if(!plan) {
			return -1;
		}

		return plan.getSimplePayRate();
	}

	/**
	 * Returns true if the eCommerce Configuration page can be skipped.
	 * This is the case for configurable products without a subscription but with simple pay possible.
	 */
	public isAddProductAndShowBasket(subscriptionType : string) {
		return this.isConfigurationNeeded() && subscriptionType === 'without' && this.getSimplePayMonthlyPrice() !== -1;
	}

	/**
	 * Retrieves the simple pay initial price (“Anzahlung”) from the provided subscriptionId and subscriptionDuration.
	 * Ecommerce as of February release 2016 does not deliver this property and it will always return 0.-
	 *
	 * @param subscriptionId - The subscription Id to check
	 * @param subscriptionDuration - The subscription duration to check
	 * @returns {string}
	 */
	// tslint:disable-next-line:prefer-function-over-method
	public getSimplePayInitialPrice() {
		return makePrice(0);
	}

	public getSubscriptions() {
		return this.subscriptions;
	}

	public getId() {
		return String(this.overrides.productId || this.product.code);
	}

	/**
	 * Returns the image URL for the given size
	 *
	 * @deprecated use @link{getImageData} to also get alt text and similar attributes
	 */
	public getImage(size : string | number) {
		const data = this.getImageData(size);

		return data.sapUrl || data.url;
	}

	/**
	 * Returns the image data objectfor the given size
	 *
	 * @see https://wiki.swisscom.com/display/ECMX/SCOM+OCC+ImageWsDTO
	 */
	public getImageData(size : string | number) : ImageData {
		if(typeof size === 'string') {
			// FIXME NEC: Is thumbnail 180 or 64?
			switch(size) {
				case 'large':
					size = 450;
					break;
				case 'big':
					size = 344;
					break;
				case 'small':
					size = 180;
					break;
				case 'mini':
					size = 104;
					break;
				default:
				case 'thumbnail':
					size = 64;
					break;
			}
		}

		return (this.images[size] || this.images[450] || {});
	}

	public getImageBig() {
		return this.overrides.imagePath || this.getImage('big');
	}

	public getImageSmall() {
		return this.overrides.imagePath || this.getImage('small');
	}

	public getImageMini() {
		return this.overrides.imagePath || this.getImage('mini');
	}

	public getRedFlagText() {
		return this.product.redFlagText;
	}

	public getSlogan() {
		return this.product.slogan;
	}

	public getFeatures() {
		return this.product.description;
	}

	public getTopFeatures() {
		// FIXME NEC: Can we get an array?
		return (this.product.topFeatures || '').split(/[\r\n]+/g);
	}

	public getDelayedDeliveryReason() {
		return this.product.deliveryInfo || '';
	}

	public getAvailability() {
		return this.availability;
	}

	public allowOnStockNotification() {
		return this.product.onStockNotifications;
	}

	public showNotificationButton() {
		return this.availability.getStatus() === AvailabilityStatus.OutOfStockNotifiable;
	}

	public isEordersProduct() {
		return this.product.isEordersProduct;
	}

	public isConfigurationNeeded() {
		return this.product.configurationNeeded;
	}

	public canHaveSubscriptions() {
		return this.product.variantType === 'MobileDeviceProduct';
	}

	public isReservationInStoreAllowed() {
		return this.product.reservationInStoreAllowed;
	}

	public isReservationAllowed() {
		return !!this.product.reservationAllowed;
	}

	public getNotifyUrl() {
		return `${this.texts.notifyurl}/${this.getId()}`;
	}

	public isSddAllowed() {
		return this.product.sddAllowed || false;
	}

	public getSddMinimumPurchaseAmount() {
		return this.family.getSddMinimumPurchaseAmount();
	}

	public getSddShippingFee() {
		return this.family.getSddShippingFee();
	}

	public hasValidSimplePay() {
		return this.subscriptions.hasValidSimplePay();
	}

	public getApplicablePromotions(subscriptionId? : string, duration? : number | string, kaze? : Case) {
		// Filter promotions that don’t match
		return this.promotions.filter(promotion => {
			return promotion.matches(subscriptionId, duration, kaze);
		});
	}

	/**
	 * Legacy mode: only returns promotions that don’t depend on the chosen subscription or its duration or the case
	 */
	public getPromotions() {
		return this.getApplicablePromotions();
	}
}

export interface VariantData {
	__ordering : string[];
	[id : string] : ProductData | string[];
}

export interface WarrantyData {
	[productId : string] : WarrantyExtension;
}

interface FamilyOverrides extends Overrides {
	products? : {
		[productId : string] : Overrides;
	}
}

/**
 * Wrapper object for product family
 */
export class Family {
	public productFamily : FamilyData;
	private details : Details;
	private overrides : FamilyOverrides;
	private ratings : Ratings;
	private texts : Texts;
	private segment : Segment;
	private products : Product[];

	constructor(
		productFamily : FamilyData,
		variants : VariantData | null = null,
		segment : Segment = Segment.RES,
		featureGroups : FeatureGroups,
		bestseller : string,
		texts : Texts,
		warranties : WarrantyData,
	) {
		this.productFamily = productFamily;
		this.overrides = {};
		this.details = new Details(undefined, undefined, productFamily);
		this.segment = segment;
		this.texts = texts;

		const createProduct = (data : ProductData) => new Product(data, this, featureGroups, bestseller, texts);

		variants = variants || {__ordering: []};

		this.products = variants.__ordering
			.filter(variantCode => variantCode in variants!)
			.map(variantCode => {
				return {
					...productFamily,
					...(variants![variantCode] as ProductData),
					...{guaranteeExtension: warranties[variantCode]},
				};
			})
			.map(createProduct);

		this.ratings = new Ratings(productFamily.code);

		// Pre-emptive loading of product ratings
		// tslint:disable-next-line:no-floating-promises
		this.ratings.load();
	}

	public getConfigError() {
		if(!this.products.length) {
			return 'No variants available.';
		}

		return null;
	}

	public setOverrides(ovr : FamilyOverrides = {}) {
		this.overrides = ovr;
		for(const productId in (this.overrides.products || {})) {
			const product = this.getProductById(productId);
			if(!product) {
				window.debug.error(`Override given for id ${productId}, but product does not exist`);
				continue;
			}
			// Inherit properties (family name) form parent but make sure to delete products
			const productOverrides = $.extend({}, this.overrides, this.overrides.products![productId], {products: null});
			product.setOverrides(productOverrides);
		}
	}

	public getProducts() {
		return this.products;
	}

	public getProductById(id : string | number) {
		for(const product of this.products) {
			if(product.getId() === String(id)) {
				return product;
			}
		}

		return null;
	}

	public getProductByFilter(color? : string, memory? : string | number, dimension? : string | number) {
		for(const product of this.products) {
			if(product.matches(color, memory, dimension)) {
				return product;
			}
		}

		return null;
	}

	public getId() {
		return String(this.productFamily.code);
	}

	public getFamilyName() {
		return this.overrides.familyName || this.productFamily.name;
	}

	public getBrand() {
		return this.productFamily.brand.code;
	}

	public getSeoName() {
		return this.texts.seoname || this.getFamilyName();
	}

	public getCategory() {
		// FIXME NEC: Does this need to be a human-readable category name?
		return this.productFamily.productCategoryMaster;
	}

	// tslint:disable-next-line:prefer-function-over-method
	public isFamilyProduct() {
		return true;
	}

	public isEordersProduct() {
		return this.productFamily.isEordersProduct;
	}

	public isConfigurationNeeded() {
		return this.productFamily.configurationNeeded;
	}

	public canHaveSubscriptions() {
		// FIXME NEC: Check that this is correct
		// See https://wiki.swisscom.com/display/ECMX/SCOM+OCC+ProductWsDTO
		// Changed from check if itemType === 'MobileDeviceProduct'…
		// …because that didn’t work; mobiles had 'Product' there instead.
		return this.productFamily.variantType === 'MobileDeviceProduct';
	}

	public getSlogan() {
		return this.productFamily.slogan || '';
	}

	public getFeatures() {
		return this.productFamily.description;
	}

	public getTopFeatures() {
		return (this.productFamily.topFeatures || '').split(/[\r\n]+/g);
	}

	public getDetailPagePath() {
		return this.texts.detailpagepath;
	}

	public getNotifyUrl() {
		return this.texts.notifyurl;
	}

	public getSegment() {
		return this.segment;
	}

	public getDetails() {
		return this.details;
	}

	public getRatings() {
		return this.ratings;
	}

	public getRedFlagText() {
		// FIXME NEC: Remove if unused (on family)
		return this.productFamily.redFlagText;
	}

	public getSddMinimumPurchaseAmount() {
		if(!this.productFamily.sddMinimumPurchaseAmount) {
			return '';
		}

		return this.productFamily.sddMinimumPurchaseAmount.toFixed(2) || '';
	}

	public getSddShippingFee() {
		if(!this.productFamily.sddShippingFee) {
			return '';
		}

		return this.productFamily.sddShippingFee.toFixed(2) || '';
	}

	public isSimplePayAllowed() {
		for(const product of this.products) {
			if(product.hasValidSimplePay()) {
				return true;
			}
		}

		return false;
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Product.ts